import FadeUp from "./FadeUp";
import {
    PlayCircle,
    Video,
    Search,
    ShieldCheck,
    TrendingUp,
    Repeat,
    TrendingDown,
    ArrowUpRight,
} from "lucide-react";

const reasons = [
    {
        icon: PlayCircle,
        title: "Productivity apps are demonstrated, not advertised.",
        body: "Nobody buys a workflow tool from a banner ad. They buy it after watching someone use it in a real setup.",
    },
    {
        icon: Video,
        title: "Workflow videos naturally sell apps.",
        body: "A 6-minute \u201chow I plan my week in Sunsama\u201d video sells more than a hundred creative variations combined.",
    },
    {
        icon: Search,
        title: "YouTube is evergreen search.",
        body: "A single YouTube integration keeps sending qualified trials for 12 to 36 months. That's not a campaign. That's an asset.",
    },
    {
        icon: ShieldCheck,
        title: "Nano creators have high trust.",
        body: "Small, credible creators inside your niche convert at 3 to 5x the rate of mega-creators or paid social.",
    },
    {
        icon: TrendingUp,
        title: "UGC lifts paid acquisition.",
        body: "The same creator content you use for organic doubles as your top-performing ads. One library, two channels.",
    },
    {
        icon: Repeat,
        title: "Affiliates become recurring acquisition partners.",
        body: "Once a creator earns from you, they keep sending trials month after month.",
    },
];

const outcomes = [
    { icon: TrendingDown, label: "Lower CAC" },
    { icon: TrendingUp, label: "Higher ROI" },
    { icon: Search, label: "Increased branded search" },
    { icon: ArrowUpRight, label: "Higher conversion rate" },
];

export const WhyCreatorMarketing = () => (
    <section
        id="why-creator-marketing"
        data-testid="why-creator-section"
        className="py-24 md:py-32 bg-[#FAFAFA] border-y border-black/[0.06]"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Why creator marketing works
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2 className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.55] md:leading-[1.55] lg:leading-[1.55] text-[#0A0A0A] text-balance">
                        Apps are bought after people see them in action, not after they're pitched.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-6 text-[15.5px] md:text-base text-gray-600 leading-[1.75] max-w-2xl">
                        Six reasons this channel outperforms almost every other one for productivity apps.
                    </p>
                </FadeUp>
            </div>

            {/* Outcomes strip */}
            <FadeUp delay={0.15}>
                <div
                    data-testid="why-outcomes-strip"
                    className="mt-10 rounded-2xl border border-black/[0.08] bg-white p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-4"
                >
                    <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-gray-500 pr-3 md:pr-4 md:border-r md:border-black/[0.08]">
                        Outcomes
                    </span>
                    {outcomes.map((o, i) => (
                        <span
                            key={i}
                            data-testid={`why-outcome-${i}`}
                            className="inline-flex items-center gap-2 rounded-full bg-[#FAFAFA] border border-black/[0.06] px-3.5 h-9 text-[13px] font-semibold text-[#0A0A0A]"
                        >
                            <o.icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                            {o.label}
                        </span>
                    ))}
                </div>
            </FadeUp>

            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {reasons.map((r, i) => (
                    <FadeUp key={i} delay={0.05 + (i % 3) * 0.05}>
                        <div
                            data-testid={`why-reason-${i}`}
                            className="h-full rounded-2xl border border-black/[0.08] bg-white p-6 md:p-7 hover:border-black/20 transition-colors"
                        >
                            <div className="h-10 w-10 rounded-xl bg-[#0A0A0A] flex items-center justify-center">
                                <r.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                            </div>
                            <h3 className="mt-5 font-display font-bold text-[17px] md:text-[18px] tracking-tight text-[#0A0A0A] leading-[1.3]">
                                {r.title}
                            </h3>
                            <p className="mt-3 text-[14.5px] text-gray-600 leading-[1.75]">
                                {r.body}
                            </p>
                        </div>
                    </FadeUp>
                ))}
            </div>
        </div>
    </section>
);

export default WhyCreatorMarketing;
